/**
 * Live Layer types + helpers — adapted from docs/tile-ui/shared/liveLayer.ts.
 *
 * The DESIGN reference lives in docs/tile-ui/. This file is the
 * production-side equivalent: same shapes, smaller surface (only what
 * the editor actually uses), and wired so it composes with the existing
 * tile-backend `/api/apps/:appId/live-layer/*` endpoints.
 *
 * Categories: the backend models category as a single string of the
 * form `''` (base) or `'cat1'` or `'cat1/cat2'`. The UI treats this as
 * the two-tuple [cat1, cat2] with `'default'` implicit at each level.
 */

import {
  LiveLayerGroup,
  LiveLayerField,
  LiveLayerFieldType,
} from '@/features/editor/live-layer/api/live-layer.api';

// ─── Re-exports so callers don't have to know about the api shim ──────────
export type LLField = LiveLayerField;
export type LLGroup = LiveLayerGroup;
export type LLSchema = LLGroup[];
export type LLFieldType = LiveLayerFieldType;

// ─── Categorisation: 2-level tuple, 'default' is implicit ────────────────
export type LLCats = [string, string];
export type LLTaxonomy = Record<string, string[]>;

/** Encode the UI's [cat1, cat2] to the backend's `'a/b'` string. */
export function catsToCategory(cats: LLCats): string {
  if (cats[0] === 'default' && cats[1] === 'default') return '';
  if (cats[1] === 'default') return cats[0];
  return `${cats[0]}/${cats[1]}`;
}

// ─── Config catalog — one row per (cat1, cat2) version ───────────────────
export interface LLConfig {
  cat1: string;
  cat2: string;
  /** Unix seconds; part of the config-{ts}.json URL. */
  ts: number;
  updatedISO: string;
  updatedBy: string;
  /** Nested body. Schema dot-paths get expanded into a tree. */
  body: Record<string, any>;
}

// ─── Fallback resolution mirrors the SDK's chain ──────────────────────────
export interface LLResolved<T = unknown> {
  value: T | undefined;
  /** Which `cat1/cat2` source the value came from. */
  source: string | null;
  /** True if the value didn't come from the active pair. */
  inherited: boolean;
}

export function llResolveValue<T = unknown>(
  cats: LLCats,
  path: string,
  configs: LLConfig[],
  dirtyBody?: Record<string, any> | null,
  /**
   * `path` → the `?? default` literal read from the app's source (see the LL
   * slice's `sourceDefaults`). Consulted LAST, so any published or pending
   * override still wins — this only fills the "nothing set anywhere" case,
   * which is what the app is really rendering.
   */
  sourceDefaults?: Record<string, unknown> | null,
): LLResolved<T> {
  const parts = path.split('.');
  const walk = (obj: any) => {
    let n = obj;
    for (const p of parts) {
      if (n == null) return undefined;
      n = n[p];
    }
    return n;
  };
  const findCfg = (a: string, b: string) => configs.find((c) => c.cat1 === a && c.cat2 === b);

  if (dirtyBody) {
    const v = walk(dirtyBody);
    if (v !== undefined)
      return { value: v as T, source: `${cats[0]}/${cats[1]}`, inherited: false };
  }
  const active = findCfg(cats[0], cats[1]);
  if (active) {
    const v = walk(active.body);
    if (v !== undefined)
      return { value: v as T, source: `${cats[0]}/${cats[1]}`, inherited: false };
  }
  const cat1Default = findCfg(cats[0], 'default');
  if (cat1Default) {
    const v = walk(cat1Default.body);
    if (v !== undefined) return { value: v as T, source: `${cats[0]}/default`, inherited: true };
  }
  const dd = findCfg('default', 'default');
  if (dd) {
    const v = walk(dd.body);
    if (v !== undefined) return { value: v as T, source: 'default/default', inherited: true };
  }
  // Nothing published anywhere — fall back to what the code itself renders.
  // Marked `inherited` and sourced as 'source' so the UI can still tell this
  // apart from a value someone actually set here.
  if (sourceDefaults) {
    const v = sourceDefaults[path];
    if (v !== undefined) return { value: v as T, source: 'source', inherited: true };
  }
  return { value: undefined, source: null, inherited: true };
}

// ─── Screen scope ─────────────────────────────────────────────────────────
// Exact-string match on `group.screens[]`. Case-sensitive on purpose —
// schema authors enforce their own casing (docs call this out). Trims
// each value so a stray trailing space in the JSON doesn't drop the
// group; if a case-only mismatch happens, we warn once with the exact
// mismatched pair so the author can fix the source.
const _warnedScreenMismatch = new Set<string>();
export function llGroupVisibleOnScreen(group: LLGroup, screen: string | 'All screens'): boolean {
  if (!screen || screen === 'All screens') return true;
  if (group.screens === undefined || group.screens === null) return true;
  if (group.screens.length === 0) return false;
  const target = screen.trim();
  const list = group.screens.map((s) => (s || '').trim());
  if (list.includes(target)) return true;

  // Diagnostic: warn once per (group, screen) pair on a case-only mismatch —
  // filter is still applied strictly. The author sees the exact string that
  // needs fixing in their live-layer.schema.json.
  const caseInsensitiveHit = list.find((s) => s.toLowerCase() === target.toLowerCase());
  if (caseInsensitiveHit) {
    const key = `${group.id}::${target}::${caseInsensitiveHit}`;
    if (!_warnedScreenMismatch.has(key)) {
      _warnedScreenMismatch.add(key);
      // eslint-disable-next-line no-console
      console.warn(
        `[LiveLayer] group "${group.id}" has screen "${caseInsensitiveHit}" but the active screen is "${target}". Fix the casing in live-layer.schema.json — screen names are case-sensitive.`,
      );
    }
  }
  return false;
}

// ─── Focus matching (canvas block → schema field) ─────────────────────────
export function llFieldMatchesFocus(field: LLField, focusKey: string | null | undefined): boolean {
  if (!focusKey) return true;
  return field.id === focusKey || field.id.indexOf(focusKey + '.') === 0;
}

// ─── Scope (App vs Tile) ──────────────────────────────────────────────────
// The schema field `group.type` is the source of truth:
//   'app'    — always visible in the App Settings tab.
//   'screen' — App Settings, but only when the active screen is in
//              `group.screens`.
//   'tile'   — auto-wired per instance; belongs in the Tile tab.
// Legacy schemas that omit `type` fall back to the id-shape heuristic:
// auto-wired tile groups use `<screenId>.<moduleName>_<line>` + a single
// `screens` entry — everything else defaults to app.
export type LLGroupScope = 'app' | 'tile';
export type LLGroupType = 'app' | 'screen' | 'tile';

export function llGroupType(group: LLGroup): LLGroupType {
  if (group.type === 'app' || group.type === 'screen' || group.type === 'tile') {
    return group.type;
  }
  const looksInstanced = /\.[a-z][\w-]*_\d+$/i.test(group.id || '');
  const hasScreens = Array.isArray(group.screens) && group.screens.length > 0;
  return looksInstanced && hasScreens ? 'tile' : 'app';
}

export function llGroupScope(group: LLGroup): LLGroupScope {
  return llGroupType(group) === 'tile' ? 'tile' : 'app';
}

/** True when the group belongs in the App Settings tab given the current
 *  screen. `type='app'` always passes; `type='screen'` requires the active
 *  screen to be listed in `screens`. `type='tile'` never passes here. */
export function llGroupVisibleInApp(group: LLGroup, activeScreen: string | undefined): boolean {
  const t = llGroupType(group);
  if (t === 'tile') return false;
  if (t === 'app') return true;
  // t === 'screen'
  if (!activeScreen || activeScreen === 'All screens') return true;
  const list = Array.isArray(group.screens) ? group.screens.map((s) => (s || '').trim()) : [];
  if (!list.length) return false;
  const target = activeScreen.trim();
  if (list.includes(target)) return true;
  // Case-insensitive nudge — the workspace already warns loudly on
  // mismatches via llGroupVisibleOnScreen; here we stay strict.
  return false;
}

/** Walk a nested object, returning the leaf at `path` or undefined. */
export function getIn(obj: unknown, path: string[]): unknown {
  let n: any = obj;
  for (const p of path) {
    if (n == null) return undefined;
    n = n[p];
  }
  return n;
}

/**
 * Deep merge for plain objects — arrays and scalars replace wholesale. A `null`
 * in the patch removes the key: it is how a draft says "not set", and apps read
 * a missing key as their own default where `null` would read as 0.
 */
export function deepMerge(
  base: Record<string, unknown>,
  patch: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...base };
  for (const [k, v] of Object.entries(patch)) {
    if (v === null) {
      delete out[k];
    } else if (
      v &&
      typeof v === 'object' &&
      !Array.isArray(v) &&
      base[k] &&
      typeof base[k] === 'object' &&
      !Array.isArray(base[k])
    ) {
      out[k] = deepMerge(base[k] as Record<string, unknown>, v as Record<string, unknown>);
    } else {
      out[k] = v;
    }
  }
  return out;
}

/** Immutable setIn — `undefined` deletes the leaf, then prunes empty parents. */
export function setIn(
  obj: Record<string, unknown>,
  path: string[],
  value: unknown,
): Record<string, unknown> {
  if (path.length === 0) return obj;
  const [head, ...tail] = path;
  const child =
    obj[head] && typeof obj[head] === 'object' && !Array.isArray(obj[head])
      ? (obj[head] as Record<string, unknown>)
      : {};
  const nextChild = tail.length === 0 ? undefined : setIn(child, tail, value);
  const next = { ...obj };
  if (tail.length === 0) {
    if (value === undefined) delete next[head];
    else next[head] = value;
  } else {
    if (nextChild && Object.keys(nextChild).length > 0) next[head] = nextChild;
    else delete next[head];
  }
  return next;
}

/** How a set of edits is written (`LiveLayerPanel`'s `onChangeFields`). */
export interface DraftEditOptions {
  /**
   * Write every edit, even one equal to what the field already shows. A layout pick sets this, so the
   * tile is pinned to every value its layout lists (decided 2026-10-20) instead of leaving equal ones to
   * whatever the published config or the app's code says later.
   */
  evenIfUnchanged?: boolean;
}

/**
 * One category's draft with `edits` (`{ fieldId: value }`) written in. An edit `isNoEdit` calls no
 * change takes the field OUT of the draft instead (`setIn` with `undefined` prunes empty parents);
 * without `isNoEdit` every edit is written.
 */
export function draftWithEdits(
  layer: Record<string, unknown>,
  edits: Record<string, unknown>,
  isNoEdit?: (fieldId: string, value: unknown) => boolean,
): Record<string, unknown> {
  let next = layer;
  for (const [fieldId, value] of Object.entries(edits)) {
    const path = fieldId.split('.');
    next = isNoEdit?.(fieldId, value) ? setIn(next, path, undefined) : setIn(next, path, value);
  }
  return next;
}

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);
/** A control with no value: an empty text box, an off switch, "Select…". */
const blankField = (v: unknown) => v === undefined || v === null || v === '' || v === false;
/** Inside a composite (spacing, border) a missing side shows as 0. */
const blankPart = (v: unknown) => blankField(v) || v === 0;

function sameShown(a: unknown, b: unknown, blank: (v: unknown) => boolean): boolean {
  if (Object.is(a, b)) return true;
  if (isPlainObject(a) || isPlainObject(b)) {
    if (!(isPlainObject(a) || blank(a)) || !(isPlainObject(b) || blank(b))) return false;
    const ao = isPlainObject(a) ? a : {};
    const bo = isPlainObject(b) ? b : {};
    const keys = new Set([...Object.keys(ao), ...Object.keys(bo)]);
    return [...keys].every((k) => sameShown(ao[k], bo[k], blankPart));
  }
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((x, i) => sameShown(x, b[i], blankField));
  }
  return blank(a) && blank(b);
}

/**
 * Would a field show `a` exactly as it shows `b`? Used to recognise an edit set
 * back to what the field showed as no edit at all.
 *
 * Structural, so a spacing object set back to None matches the unset value it
 * started from, and blank-aware, so typing into an empty Message and deleting
 * it again doesn't leave `""` behind — an override that hid the whole bar.
 * Top-level 0 is NOT blank: an empty number field and a 0 read differently.
 */
export function llSameShownValue(a: unknown, b: unknown): boolean {
  return sameShown(a, b, blankField);
}

// ─── Blob keys ────────────────────────────────────────────────────────────
// Mirror tile-backend's src/config/aws.ts. Keys are relative to the CDN host:
//   live-layer/<app>/<env>/manifest.json
//   live-layer/<app>/<env>/<cat1>/<cat2>/config-<version>.json
export function llEnvPrefix(appId: string, env: string): string {
  return `live-layer/${appId}/${env}`;
}

/** Both levels always spelled out: '' → default/default, 'in' → in/default. */
export function llCategorySegments(category: string): [string, string] {
  const [c1 = 'default', c2 = 'default'] = category.split('/').filter(Boolean);
  return [c1, c2];
}

export function llBlobKey(appId: string, env: string, category: string, version: number): string {
  return `${llEnvPrefix(appId, env)}/${llCategorySegments(category).join(
    '/',
  )}/config-${version}.json`;
}

export function llManifestKey(appId: string, env: string): string {
  return `${llEnvPrefix(appId, env)}/manifest.json`;
}

// ─── Env-derived host ─────────────────────────────────────────────────────
export type EnvKindFull = 'dev' | 'prod';

/**
 * Every Live Layer artifact — dev and prod — is served through the same
 * bucket + CDN (`cdn-demo.tile.dev`), separated by path (`.../<env>/...`).
 * One host for both envs; the path segment is what the SDK uses to pick.
 */
export const LIVE_LAYER_CDN = 'https://cdn-demo.tile.dev';

/** Kept as a helper so callers can stay symmetric with old code. */
export function llHostForEnv(_kind: EnvKindFull): string {
  return LIVE_LAYER_CDN;
}

// ─── Env display ──────────────────────────────────────────────────────────
export const ENV_COLOR: Record<EnvKindFull, string> = {
  dev: '#3B82F6',
  prod: '#16A34A',
};
export const ENV_LABEL: Record<EnvKindFull, string> = {
  dev: 'DEV',
  prod: 'PROD',
};
